import { useMemo } from "react";
import useStore from "@/stores";
import { useClassroomAppControlsQuery } from "@/query/classroomAppControlsQuery";
import { useClassroomRulesQuery } from "@/query/classroomRulesQuery";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { countActiveRules, mergeInClassRules, toAppliedList } from "./utils";

interface Args {
	enabled: boolean;
	applianceId: string;
}

/** Active Rules total for the class toolbar button. No-ops when the new drawer flag is off. */
export function useActiveRulesCount(args: Args): number {
	const { enabled, applianceId } = args;
	// Read from the same store the drawer uses, so the badge and the drawer share one query cache entry.
	const classroomStableIdV1 = useSelectedClassroomStore((state) => state.stableIdV1) ?? "";
	const classroomSourceName = useSelectedClassroomStore((state) => state.sourceId) ?? "";
	const appControlsEnabled = useStore((state) => state.featureToggles.isNativeAppControlsEnabled());
	const classroomRulesQuery = useClassroomRulesQuery(applianceId, classroomStableIdV1, enabled);
	const appControlsQuery = useClassroomAppControlsQuery({
		classroomName: classroomSourceName,
		classroomStableIdV1,
		enabled: enabled && appControlsEnabled,
	});

	return useMemo(() => {
		const lists = (classroomRulesQuery.data?.appliedLists ?? []).map(toAppliedList);
		const appControlRows = appControlsEnabled ? (appControlsQuery.data ?? []) : [];
		const inClassRules = mergeInClassRules(classroomRulesQuery.data?.inClassRules ?? [], appControlRows);
		return countActiveRules(inClassRules, lists);
	}, [classroomRulesQuery.data, appControlsQuery.data, appControlsEnabled]);
}
